"use client";

/**
 * Beep curto sintetizado via Web Audio API — zero asset, sem fetch, sem
 * permission de notification. Funciona offline e em qualquer browser moderno.
 *
 * Serve aos eventos de notificacao que nao tem arquivo de audio proprio
 * (`conversaAtribuida`, `mensagemNova`, `alertaCritico`) — ver o mapeamento em
 * `lib/notification-sounds.ts`, unico consumidor desta funcao.
 *
 * O hook `useChatAudioAlert` que vivia aqui foi substituido por
 * `useNotificationSound`, que cobre os cinco eventos em vez de so a atribuicao
 * direta. A sintese do beep permaneceu por ser boa: nenhum byte trafegado.
 */

/**
 * Toca uma sequencia de notas com Web Audio API. Falha silenciosa.
 *
 * Cada nota e `[frequenciaHz, atrasoEmSegundos]`. Um unico AudioContext serve a
 * sequencia inteira e e liberado quando a ultima nota termina — criar um por
 * nota acumularia contexts (browsers limitam a ~6 simultaneos).
 */
function playTones(notas: Array<[number, number]>): void {
  if (typeof window === "undefined") return;
  try {
    type WebkitWindow = typeof window & {
      webkitAudioContext?: typeof AudioContext;
    };
    const Ctor =
      window.AudioContext ?? (window as WebkitWindow).webkitAudioContext;
    if (!Ctor) return;

    const ctx = new Ctor();
    const inicio = ctx.currentTime;
    let ultimoFim = inicio;

    for (const [freq, atraso] of notas) {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = "sine";
      oscillator.frequency.value = freq;

      // Envelope curto com fade out — evita o "clique" de corte abrupto.
      const t = inicio + atraso;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.15, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);

      oscillator.connect(gain);
      gain.connect(ctx.destination);

      oscillator.start(t);
      oscillator.stop(t + 0.3);
      ultimoFim = t + 0.3;

      // Libera o context so na ultima nota.
      if (ultimoFim === t + 0.3 && atraso === notas[notas.length - 1][1]) {
        oscillator.onended = () => {
          ctx.close().catch(() => {
            // Ignora — context ja pode estar fechado.
          });
        };
      }
    }
  } catch {
    // AudioContext indisponivel ou autoplay bloqueado — falha silenciosa.
  }
}

/**
 * Assinaturas sonoras dos eventos sem arquivo de audio. Sao MELODICAMENTE
 * distintas de proposito: o atendente reconhece o evento sem olhar a tela, que e
 * o que o card de preferencias promete. Um beep unico pra todos tornaria
 * indistinguivel "atribuiram uma conversa" de "cliente respondeu".
 */

/** Atribuicao direta: 1 nota 880Hz (A5). Som historico, preservado. */
export function playAssigneeBeep(): void {
  playTones([[880, 0]]);
}

/** Mensagem nova: 1 nota curta e mais grave (520Hz) — discreta, alta frequencia de uso. */
export function playMessageBeep(): void {
  playTones([[520, 0]]);
}

/** Alerta critico: 3 notas DESCENDENTES — padrao universal de "algo quebrou". */
export function playCriticalBeep(): void {
  playTones([
    [740, 0],
    [620, 0.18],
    [480, 0.36],
  ]);
}

